<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { getRecipientViewUrlApi } from '@Api/esignature.api'
import { markSigningCompleteObserved } from '@Services/signingCompletionGate'
import { setZendeskLauncherVisible } from '@Src/shared/utils/zendeskVisibility'

const router = useRouter();

const isLoading = ref(true);
const isRedirecting = ref(false);
const recipientViewUrl = ref<string | null>(null);
const docusignRecipientFrame = ref<HTMLIFrameElement | null>(null);
let checkInterval: ReturnType<typeof setInterval> | null = null;

// Detect iOS devices
const isIOS = computed(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
});

const docusignEditorUrl = computed(() => {
    return recipientViewUrl.value;
});

// Router guard already validated signing request status (requiresSigningValidation: true)
// This component only needs to load the DocuSign URL
const loadRecipientViewUrl = async () => {
    try {
        isLoading.value = true;

        recipientViewUrl.value = await getRecipientViewUrlApi();

        if (!recipientViewUrl.value) {
            throw new Error("No recipient view URL received from API");
        }

    } catch (error) {
        console.error("Failed to load DocuSign recipient view:", error);
        isLoading.value = false;
    }
};

const handleRedirect = (routeName: string) => {
    if (isRedirecting.value) {
        return;
    }

    isRedirecting.value = true;
    stopPolling(); // Stop polling once redirect is detected

    // On iOS, wait longer before hiding iframe and navigating to ensure redirect completes
    const delay = isIOS.value ? 500 : 100;

    setTimeout(() => {
        router.push({ name: routeName });
    }, delay);
};

const checkIframeUrl = () => {
    if (!docusignRecipientFrame.value || isRedirecting.value) {
        return;
    }

    try {
        const iframe = docusignRecipientFrame.value;

        const iframeUrl = iframe.contentWindow?.location.href?.toLowerCase();

        // DocuSign will redirect to /esignature/submit when signing is complete
        if (iframeUrl) {
            if (iframeUrl.includes('/esignature/submit?event=signing_complete')) {

                markSigningCompleteObserved();
                handleRedirect('complete');
                return;
            }
            if (iframeUrl.includes('/esignature/submit?event=decline')) {
                handleRedirect('decline');
                return;
            }
            if (iframeUrl.includes('/esignature/submit?event=viewing_complete') ||
                iframeUrl.includes('/esignature/submit?event=cancel')) {
                handleRedirect('finish-later');
                return;
            }
        }
    } catch (error) {
        // Cross-origin access error occurs when iframe is on DocuSign's domain (e.g., docusign.net)
        // This is expected due to the Same-Origin Policy - we cannot access iframe.contentWindow.location
        // when the iframe is from a different origin. Once DocuSign redirects to our domain
        // (/esignature/submit), the iframe becomes same-origin and we can access its URL.
        // We'll continue checking on the next load event when the iframe redirects to our domain.
        // Error is intentionally ignored as it's expected behavior during cross-origin phase.
        if (process.env.NODE_ENV === 'development') {
            console.debug('Cross-origin iframe access blocked (expected):', error);
        }
    }
};

// Polling mechanism for iOS to reliably detect redirect
const startPolling = () => {
    if (checkInterval) {
        return;
    }

    // On iOS, poll more frequently to catch the redirect quickly
    const pollInterval = isIOS.value ? 200 : 500;

    checkInterval = setInterval(() => {
        checkIframeUrl();
    }, pollInterval);
};

const stopPolling = () => {
    if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
    }
};

const onEditorLoad = () => {
    if (recipientViewUrl.value) {
        isLoading.value = false;
    }

    // Check if DocuSign has redirected to our return URL
    // This happens when signing is complete
    checkIframeUrl();

    // Start polling on iOS for more reliable detection
    if (isIOS.value) {
        startPolling();
    }
};

const onEditorError = () => {
    console.error('Failed to load Docusign editor');
    isLoading.value = false;
    stopPolling();
};

onMounted(() => {
    loadRecipientViewUrl();
    setZendeskLauncherVisible(false);
});

onUnmounted(() => {
    stopPolling();
    setZendeskLauncherVisible(true);
});

</script>

<template>
    <div class="sign-document-step">
        <div
            v-if="isLoading"
            class="loading-container"
        >
            <app-page-loader>
                <div>Loading secure environment...</div>
            </app-page-loader>
        </div>
        <iframe
            v-if="docusignEditorUrl"
            v-show="!isLoading && (!isRedirecting || isIOS)"
            ref="docusignRecipientFrame"
            :src="docusignEditorUrl"
            class="recipient-frame"
            :class="{ 'ios-redirecting': isIOS && isRedirecting }"
            title="Review and complete"
            @load="onEditorLoad"
            @error="onEditorError"
        >
        </iframe>
    </div>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.loading-container {
    height: 100%;
    min-height: 400px;
}

.sign-document-step {
    height: 100vh;
    width: 100%;
    background: white;
}

.recipient-frame {
    width: 100%;
    height: 100%;
    min-height: 600px;
    border: none;
    overflow: hidden;

    &.ios-redirecting {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: -9999px;
        left: -9999px;
    }
}


@include tablet-and-down {
    .recipient-frame {
        min-height: 500px;
    }
}
</style>
